import React, { Fragment, useState, useEffect } from 'react';
import { size, isFunction, find, get } from 'lodash/fp';
import PropTypes from 'prop-types';
import { Table } from 'rsuite';
import { useDispatch, useSelector } from 'react-redux';
import {
  deleteInventoryItemAvailability,
  deleteInventoryPackageAvailability
} from '../../../redux/Inventory/sagas/routines';
import getItemAvailabilityUniqId from '../../../helpers/getItemAvailabilityUniqId';
import { getIsDeleting as getIsDeletingItemAvailability } from '../../../redux/Inventory/selectors/item/availability';
import { getIsDeleting as getIsDeletingPackageAvailability } from '../../../redux/Inventory/selectors/package/availability';
import {
  ItemType,
  PackageType
} from '../../../constants/inventory/InventoryTypes';
import DeleteConfirmationModal from '../../DeleteConfirmationModal';
import PathKeyCell from '../../widgets/custom-cell/PathKey';
import CheckCell from './CheckCell';
import DeleteCell from './DeleteCell';

const { Column, HeaderCell, Cell } = Table;

const computeHeight = (itemsCount = 0, rowHeight = 42, initialHeight = 60) => {
  const maxCount = 5;
  const count = itemsCount >= maxCount ? maxCount : itemsCount;
  return initialHeight + count * rowHeight;
};

const getSelectedItem = (selected, availability) => {
  const pathByType = {
    [ItemType]: 'zone.id',
    [PackageType]: 'location.id'
  };
  return find(
    each => get(pathByType[each.type], each) === selected,
    availability
  );
};

function InventoryItemAvailability({
  availability,
  onSelectItem,
  canDelete,
  selected
}) {
  const dispatch = useDispatch();
  const [currentSelected, setCurrentSelected] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const isDeletingItem = useSelector(getIsDeletingItemAvailability);
  const isDeletingPackage = useSelector(getIsDeletingPackageAvailability);
  const hasSelectItem = isFunction(onSelectItem);
  const isItemType = get('0.type', availability) === ItemType;
  useEffect(
    () => {
      setCurrentSelected(getSelectedItem(selected, availability));
      return () => {
        setCurrentSelected(null);
      };
    },
    [availability, selected]
  );

  const handleSelectItem = value => {
    setCurrentSelected(getSelectedItem(value, availability));
    onSelectItem(value);
  };

  const handleHideDeleteModal = () => {
    setShowDeleteConfirmation(true);
  };

  const handleShowDeleteModal = value => {
    setItemToDelete(value);
    setShowDeleteConfirmation(true);
  };

  const handleConfirmDeleteItem = () => {
    if (ItemType === itemToDelete.type) {
      dispatch(deleteInventoryItemAvailability(itemToDelete));
    } else {
      dispatch(deleteInventoryPackageAvailability(itemToDelete));
    }
  };

  const handleIsDeleting = value => {
    if (!itemToDelete) return false;
    const isSameItem =
      getItemAvailabilityUniqId(value) ===
      getItemAvailabilityUniqId(itemToDelete);

    return (
      (ItemType === isItemType ? isDeletingItem : isDeletingPackage) &&
      isSameItem
    );
  };

  return (
    <Fragment>
      <DeleteConfirmationModal
        message={`Click "Yes" to proceed the removal of selected 0 availabilty?`}
        show={showDeleteConfirmation}
        onHide={handleHideDeleteModal}
        onConfirm={handleConfirmDeleteItem}
        isDeleting={isItemType ? isDeletingItem : isDeletingPackage}
      />

      <Table
        data={availability}
        width={hasSelectItem || canDelete ? 420 : 370}
        height={computeHeight(size(availability))}
        onRowClick={hasSelectItem ? handleSelectItem : () => {}}
      >
        <Column width={isItemType ? 100 : 200}>
          <HeaderCell>Location</HeaderCell>
          <PathKeyCell dataKey="location.name" />
        </Column>
        {isItemType && (
          <Column width={100}>
            <HeaderCell>Zone</HeaderCell>
            <PathKeyCell dataKey="zone.name" />
          </Column>
        )}
        <Column width={50} align="right">
          <HeaderCell>Qty</HeaderCell>
          <Cell dataKey="quantity" />
        </Column>
        <Column width={85} align="right">
          <HeaderCell>Onhold Qty</HeaderCell>
          <Cell dataKey="onholdQuantity" />
        </Column>
        {hasSelectItem && (
          <Column width={35}>
            <HeaderCell />
            <CheckCell dataKey="location.name" selectedItem={currentSelected} />
          </Column>
        )}

        {canDelete && (
          <Column width={60}>
            <HeaderCell>Action</HeaderCell>
            <DeleteCell
              dataKey="deletedAt"
              onClick={handleShowDeleteModal}
              isDeleting={handleIsDeleting}
            />
          </Column>
        )}
      </Table>
    </Fragment>
  );
}

InventoryItemAvailability.propTypes = {
  availability: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  selected: PropTypes.number,
  onSelectItem: PropTypes.func,
  canDelete: PropTypes.bool
};

InventoryItemAvailability.defaultProps = {
  selected: null,
  onSelectItem: null,
  canDelete: false
};

export default InventoryItemAvailability;
